import { UserOutlined } from '@ant-design/icons';
import { Input, InputNumber, Select, Switch } from 'antd';

import { BabyLogo } from 'app/components/commons/icons/BabyLogo';
import type { BedCategory, Room, RoomCategory } from 'app/typings';
import { hasBabyBed, maxRoomCapacity } from 'app/utils/room';
import { pluralize } from 'app/utils/strings';

const { Option } = Select;

const PACKAGE_FORM_NAMES = [
  'categoryId',
  'name',
  'categoryRoomCount',
  'area',
  'maxRoomCapacity',
  'hasPRM',
  'beds',
] as const;

type PackageFormName = (typeof PACKAGE_FORM_NAMES)[number];

type BaseFieldConfig = {
  label: string;
  name: PackageFormName;
  required: boolean;
  description?: string;
  valuePropName?: string;
};

type FieldConfig = BaseFieldConfig &
  (
    | {
        type: 'select';
        placeholder: string;
        getOptions: (
          roomCategories?: RoomCategory[]
        ) => { id: number; name: string }[];
        getDisplayValue: (value: number, options?: RoomCategory[]) => string;
      }
    | {
        type: 'text';
        placeholder: string;
        getDisplayValue: (formValues: Room) => string;
      }
    | {
        type: 'number';
        min?: number;
        className?: string;
        prefix?: string;
        getDisplayValue: (formValues: Room) => string;
      }
    | {
        type: 'switch';
      }
    | {
        type: 'custom';
        renderEdit: (
          formValues: Room,
          bedCategories?: BedCategory[]
        ) => React.ReactNode;
        renderReadOnly: (
          formValues: Room,
          bedCategories?: BedCategory[]
        ) => React.ReactNode;
      }
  );

const FIELD_CONFIGS: FieldConfig[] = [
  {
    label: 'Category type',
    name: 'categoryId',
    required: true,
    type: 'select',
    placeholder: 'Select a category',
    getOptions: (roomCategories) => roomCategories || [],
    getDisplayValue: (value, roomCategories) =>
      roomCategories?.find((category) => category.id === value)?.name || '',
  },
  {
    label: 'Room name',
    name: 'name',
    required: false,
    description:
      'If this category has a specific name, write it in your local language',
    type: 'text',
    placeholder: 'Enter room name',
    getDisplayValue: (formValues) => formValues.name || '',
  },
  {
    label: 'Category room count',
    name: 'categoryRoomCount',
    required: true,
    type: 'number',
    getDisplayValue: (formValues) => String(formValues.categoryRoomCount || ''),
  },
  {
    label: 'Room size',
    name: 'area',
    required: true,
    type: 'number',
    min: 0,
    className: 'reverse-prefix',
    prefix: 'm²',
    getDisplayValue: (formValues) => `${formValues.area || 0} m²`,
  },
  {
    label: 'Max. room capacity',
    name: 'maxRoomCapacity',
    required: false,
    description: 'The room capacity depends on your bed capacity',
    type: 'custom',
    renderEdit: (formValues, bedCategories) => (
      <div className="maxRoomContainer">
        <InputNumber
          type="number"
          className="reverse-prefix"
          disabled
          value={maxRoomCapacity(formValues.beds, bedCategories ?? [])}
          prefix={<UserOutlined />}
        />
        {hasBabyBed(formValues.beds) && (
          <InputNumber
            disabled
            className="reverse-prefix"
            value={1}
            prefix={<BabyLogo color="#B3B3B3" />}
          />
        )}
      </div>
    ),
    renderReadOnly: (formValues, bedCategories) => (
      <>
        {pluralize(
          maxRoomCapacity(formValues.beds, bedCategories ?? []),
          'guest',
          'guests'
        )}
        {hasBabyBed(formValues.beds) && ' + 1 baby'}
      </>
    ),
  },
  {
    label: 'PRM access',
    name: 'hasPRM',
    required: false,
    type: 'switch',
    valuePropName: 'checked',
  },
];

type PackageFormField = {
  label: string;
  name: PackageFormName;
  required: boolean;
  description?: string;
  component: React.ReactNode;
  valuePropName?: string;
};

const renderFieldComponent = (
  config: FieldConfig,
  formValues: Room,
  roomCategories?: RoomCategory[],
  bedCategories?: BedCategory[],
  isEditMode?: boolean
): React.ReactNode => {
  if (!isEditMode) {
    switch (config.type) {
      case 'select':
        return (
          <>{config.getDisplayValue(formValues.categoryId, roomCategories)}</>
        );
      case 'text':
      case 'number':
        return <>{config.getDisplayValue(formValues)}</>;
      case 'switch':
        return <Switch disabled checked={formValues.hasPRM} />;
      case 'custom':
        return config.renderReadOnly(formValues, bedCategories);
      default:
        return null;
    }
  }

  switch (config.type) {
    case 'select':
      return (
        <Select
          placeholder={config.placeholder}
          showSearch
          optionFilterProp="children"
        >
          {config.getOptions(roomCategories)?.map((option) => (
            <Option key={option.id} value={option.id}>
              {option.name}
            </Option>
          ))}
        </Select>
      );
    case 'text':
      return <Input placeholder={config.placeholder} />;
    case 'number':
      return (
        <InputNumber
          className={config.className}
          type="number"
          prefix={config.prefix}
          min={config.min}
        />
      );
    case 'switch':
      return <Switch />;
    case 'custom':
      return config.renderEdit(formValues, bedCategories);
    default:
      return null;
  }
};

export const PackageFormFields = (
  formValues: Room,
  roomCategories?: RoomCategory[],
  bedCategories?: BedCategory[],
  isEditMode?: boolean
): PackageFormField[] =>
  FIELD_CONFIGS.map((config) => ({
    label: config.label,
    name: config.name,
    required: config.required,
    description: config.description,
    valuePropName: config.valuePropName,
    component: renderFieldComponent(
      config,
      formValues,
      roomCategories,
      bedCategories,
      isEditMode
    ),
  }));
